import type { Space } from '@cocoonspace/sdk-js/types/space'
import { Button } from '@cocoonspace/ui/components/button'
import { useLingui } from '@lingui/react/macro'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { toast } from 'sonner'

import { useCart } from '~/domains/cart/cart-provider'
import {
	BookingOption,
	SpaceNotAvailableError,
	useAddToCart,
} from '~/domains/cart/use-add-to-cart.hook'
import { useIsRouterLoading } from '~/hooks/use-is-router-loading.hook'
import { useHttpErrorMsg } from '~/lib/get-http-error-msg'
import { useAuth } from '~/providers/auth-provider'

export const AddToCartButton = ({
	space,
	bookingOptions,
	onSuccess,
}: {
	space: Space
	bookingOptions: BookingOption[]
	onSuccess?: () => void
}) => {
	const router = useRouter()
	const pathname = usePathname() ?? ''
	const searchParams = useSearchParams()
	const isRouterLoading = useIsRouterLoading()
	const httpErrorMsg = useHttpErrorMsg()

	const { t } = useLingui()
	const { isAuth, setAuthErrorMsg, openRegister } = useAuth()
	const { setIsSheetOpen } = useCart()

	const { mutateAsync: addToCart, isPending } = useAddToCart({ space })

	const redirectIfNotAuth = () => {
		const params = new URLSearchParams(searchParams.toString())

		params.delete('day')
		params.delete('start')
		params.delete('end')

		if (bookingOptions.length > 1) {
			for (const option of bookingOptions) {
				params.append('day', option.day)
			}
		} else if (bookingOptions[0]) {
			params.set('day', bookingOptions[0].day)
			if (bookingOptions[0].start) {
				params.set('start', bookingOptions[0].start)
			}
			if (bookingOptions[0].end) {
				params.set('end', bookingOptions[0].end)
			}
		}

		router.push(`${pathname}?${params.toString()}`)

		openRegister()
		setAuthErrorMsg(t`Vous devez être connecté(e) pour réserver un Cocoon`)
		return
	}

	const onError = (error: any) => {
		if (error instanceof SpaceNotAvailableError) {
			return toast.error(
				t`Certains dates sélectionnées ne sont plus disponibles et ont été retirées de votre sélection.`,
			)
		}

		const errorCode = error?.response?.data?.errors?.[0].code as
			| string
			| undefined

		toast.error(
			httpErrorMsg[`api.${errorCode}`]
				? httpErrorMsg[`api.${errorCode}`]()
				: t`Une erreur est survenue`,
		)
	}

	const onAddToCart = () => {
		if (space.status !== 'published') return
		if (!isAuth) return redirectIfNotAuth()

		return addToCart({ bookingOptions })
			.then(() => {
				onSuccess?.()
				setIsSheetOpen(true)
			})
			.catch((err) => onError(err))
	}

	return (
		<Button
			disabled={isRouterLoading || isPending || bookingOptions.length === 0}
			onClick={onAddToCart}
			size='sm'
			variant='outlinePrimary'
			className='ml-auto'
		>
			{t`Ajouter au panier`}
		</Button>
	)
}
